import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { RestServiceConstants } from "../../constants/rest-service.constants";
import { RestClient } from "../rest/client/rest.client";
import { ResponseBody } from "../rest/decorators/response-body.decorator";
import { Response } from "../rest/decorators/response.decorator";
import { RequestBody, Query, PlainBody } from "../rest/request/params";
import { Headers } from "../rest/decorators/headers.decorator";
import { GET, POST } from "../rest/request/methods";
import { DMVConstants } from "../../constants/dmv.constants";
import { RegistrationResponse } from "../../models/dmv/registration.response";
import { StringUtil } from "../../utils/string.util";
import { AgencyDmvConfigResponse } from "src/app/models/dmv/agency-config.response";
import { TagCatalogResponse } from "src/app/models/dmv/tagCatalogResponse";
import { DmvCostDataResponse } from "src/app/models/dmv/dmvCostDataResponse";
import { DonationOrgsResponse } from "src/app/models/dmv/donationOrgsResponse";
import { RegistrationTypesResponse } from "src/app/models/dmv/registration-types.response";
import { ServiceFeeAmountResponse } from "src/app/models/dmv/service-fee-amount.response";
import { TransactionIDResponse } from "src/app/models/dmv/transactionID.response";
import { RenewalResponse } from "src/app/models/dmv/renewal-response";

import { AccessTokenResponse } from "src/app/models/dmv/access-token-response";
import { Renewal } from "src/app/models/dmv/renewal";
import { Address } from "src/app/models/dmv/address";
import { Donation } from "src/app/models/dmv/donation";
import { NotificationOptions } from "src/app/models/dmv/notificationOptions";
import { RenewalItem } from "src/app/models/dmv/renewalItem";
import { AppConfigUtil } from "../../utils/app-config.util";
import { FundingToken } from "src/app/models/dmv/fundingToken";

import { DMVStoreService } from "src/app/services/dmv/dmv-store.service";

import { HttpClient, HttpResponse } from "@angular/common/http";
import {
	DmvCostData,
	ErrorLogRequest,
	ErrorRequest
} from "src/app/models/dmv/models";
import { LogInResponse } from "src/app/models/user/log-in.response";

@Injectable({ providedIn: "root" })
export class DMVDataService extends RestClient {
	private dmvRegistrationResponse$: Observable<RegistrationResponse>;

	@Headers({
		Accept: RestServiceConstants.APPLICATION_JSON,
		"content-type": RestServiceConstants.APPLICATION_JSON
	})
	@GET()
	@ResponseBody<any>()
	public getRegistration(
		@Query("pin") pin?: string,
		@Query("dateOfBirth") dateOfBirth?: string,
		@Query("licensePlate") licensePlate?: string,
		@Query("expirationDate") expirationDate?: string,
		@Query("pageSize") pageSize?: number,
		@Query("targetPageNumber") targetPageNumber?: number,
		@Query("qrCode") qrCode?: boolean,
		@Query("token") token?: string
	): Observable<any> {
		return;
	}

	public getRegistrationResponse(
		agencyID: string,
		pin: string,
		dateOfBirth: string,
		licensePlate: string,
		expirationDate: string,
		pageSize: number,
		targetPageNumber: number,
		sessionID: string,
		qrCode: boolean,
		token: string
	): Observable<RegistrationResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.REGISTRATION_URI, agencyID, sessionID)
		);

		return this.getRegistration(
			pin,
			dateOfBirth,
			licensePlate,
			expirationDate,
			pageSize,
			targetPageNumber,
			qrCode,
			token
		);
	}

	@GET()
	@ResponseBody<any>()
	public getAgencyConfig(): Observable<any> {
		return;
	}

	public getAgencyConfigResponse(
		agencyID: string,
		sessionID: string
	): Observable<AgencyDmvConfigResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.AGENCY_CONFIGURATION_URI,
				agencyID,
				sessionID
			)
		);

		return this.getAgencyConfig();
	}

	@GET()
	@ResponseBody<any>()
	public getDmvCost(
		@Query("newTagCode") newTagCode?: string,
		@Query("renewalOptionID") renewalOptionID?: number
	): Observable<any> {
		return;
	}

	public getDmvCostResponse(
		newTagCode: string,
		renewalOptionID: number,
		agencyID: string,
		registrationID: number,
		sessionID: string
	): Observable<DmvCostDataResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.DMV_COST_URI,
				agencyID,
				registrationID,
				sessionID
			)
		);

		return this.getDmvCost(newTagCode, renewalOptionID);
	}

	@GET()
	@ResponseBody<any>()
	public getDonationOrgs(): Observable<any> {
		return;
	}

	public getDonationOrgsResponse(
		sessionID: string
	): Observable<DonationOrgsResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.DONATION_ORGS_URI, sessionID)
		);

		return this.getDonationOrgs();
	}

	@GET()
	@ResponseBody<any>()
	public getRegistrationTypes(): Observable<any> {
		return;
	}

	public getRegistrationTypesResponse(
		sessionID: string
	): Observable<RegistrationTypesResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.REGISTRATION_TYPES_URI, sessionID)
		);

		return this.getRegistrationTypes();
	}

	@GET()
	@Response<any>()
	public getRegistrationCost(
		@Query("registrationType") registrationType: string,
		@Query("renewalOptionID") renewalOptionID?: number
	): Observable<HttpResponse<DmvCostDataResponse>> {
		return;
	}

	public getRegistrationCostResponse(
		agencyID: string,
		sessionID: string,
		registrationType: string,
		renewalOptionID: number
	): Observable<HttpResponse<DmvCostDataResponse>> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.DUPLICATE_REGISTRATION,
				agencyID,
				sessionID,
				"0"
			)
		);

		return this.getRegistrationCost(registrationType, renewalOptionID);
	}

	@GET()
	@ResponseBody<any>()
	public getTagCatalog(): Observable<any> {
		return;
	}

	public getTagCatalogResponse(
		sessionID: string
	): Observable<TagCatalogResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.TAG_CATALOG_URI, sessionID)
		);

		return this.getTagCatalog();
	}

	// @GET()
	// @ResponseBody<any>()
	// public getRenewal(

	// @Query("renewalToken") renewalToken?: string,
	// @Query("renewalItems") renewalItems?: Array<RenewalItem>,
	// @Query("donations") donations?: Array<Donation>,
	// @Query("megAppFee") megAppFee?: number,
	// @Query("megAppFeeAbsorbed") megAppFeeAbsorbed?: boolean,
	// @Query("serviceProviderFee") serviceProviderFee?: number,
	// @Query("deliveryMethod") deliveryMethod?: Renewal.DeliveryMethodEnum,
	// @Query("pickupLocationID") pickupLocationID?: string,
	// @Query("pickupPersonName") pickupPersonName?: string,
	// @Query("mailToAddress") mailToAddress?: Address,
	// @Query("updateAddressOnFile") updateAddressOnFile?: boolean,
	// @Query("fundingToken") fundingToken?: string,
	// @Query("notificationOptions") notificationOptions?: NotificationOptions,
	// @Query("comment") comment?: string
	// ): Observable<any> {
	// 	return;
	// }

	// public getRenewalResponse(request:Renewal, agencyID): Observable<RenewalResponse> {

	// 	this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
	// 	this.setDynamicUri(

	// 		StringUtil.format(DMVConstants.RENEWAL, agencyID)

	// 	);

	// 	return this.getRenewal(
	// 	request.renewalToken,
	// 	request.renewalItems,
	// 	request.donations,
	// 	request.megAppFee,
	// 	request.megAppFeeAbsorbed,
	// 	request.serviceProviderFee,
	// 	request.deliveryMethod,
	// 	request.pickupLocationID,
	// 	request.pickupPersonName,
	// 	request.mailToAddress,
	// 	request.updateAddressOnFile,
	// 	request.fundingToken,
	// 	request.notificationOptions,
	// 	request.comment
	// 	);
	// }

	@POST()
	@ResponseBody<LogInResponse>()
	public getFundingToken(
		@RequestBody request: any,
		@Query("webComponentLoadTime") webComponentLoadTime?: number,
		@Query("webComponentTokenizationTime") webComponentTokenizationTime?: number
	): Observable<any> {
		return;
	}

	public getFundingTokenResponse(
		request,
		agencyID,
		sessionID: string,
		webComponentLoadTime?: number,
		webComponentTokenizationTime?: number
	): Observable<LogInResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.FUNDING_TOKEN, agencyID, sessionID)
		);

		return this.getFundingToken(
			request,
			webComponentLoadTime,
			webComponentTokenizationTime
		);
	}

	@GET()
	@ResponseBody<any>()
	public getPaymentAccessToken(): Observable<any> {
		return;
	}

	public getPaymentAccessTokenResponse(
		agencyID,
		sessionID: string
	): Observable<AccessTokenResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.PAYMENT_ACCESS_TOKEN, agencyID, sessionID)
		);

		return this.getPaymentAccessToken();
	}

	@POST()
	@ResponseBody<any>()
	public postError(@RequestBody request: any): Observable<any> {
		return;
	}

	public postErrorResponse(
		request: any,
		agencyID,
		sessionID: string
	): Observable<any> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.ERROR, agencyID, sessionID)
		);

		return this.postError(request);
	}

	@POST()
	@ResponseBody<any>()
	public getRenewal(@RequestBody request: any): Observable<any> {
		return;
	}

	public getRenewalResponse(
		request: Renewal,
		agencyID,
		sessionID: string
	): Observable<RenewalResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(DMVConstants.RENEWAL, agencyID, sessionID)
		);

		return this.getRenewal(request);
	}

	@POST()
	@ResponseBody<any>()
	public updateRenewal(@RequestBody request: any): Observable<any> {
		return;
	}

	public getUpdatedRenewalResponse(
		request: Renewal,
		agencyID,
		renewToken: string,
		sessionID: string
	): Observable<RenewalResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.UPDATE_RENEWAL,
				agencyID,
				renewToken,
				sessionID
			)
		);

		return this.updateRenewal(request);
	}

	@GET()
	@ResponseBody<any>()
	public getServiceFees(): Observable<any> {
		return;
	}

	public getServiceFeesResponse(
		renewalToken: string,
		agencyID: string,
		sessionID: string
	): Observable<ServiceFeeAmountResponse> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.SERVICE_FEE_AMOUNT,
				agencyID,
				renewalToken,
				sessionID
			)
		);

		return this.getServiceFees();
	}

	@POST()
	@Response<any>()
	public getRenewalSubmit(): Observable<any> {
		return;
	}

	public getRenewalSubmitResponse(
		renewalToken: string,
		agencyID: string,
		sessionID: string
	): Observable<HttpResponse<TransactionIDResponse>> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.RENEWAL_SUBMIT,
				agencyID,
				renewalToken,
				sessionID
			)
		);

		return this.getRenewalSubmit();
	}

	@POST()
	@ResponseBody<any>()
	public postErrorLog(@RequestBody request: any): Observable<any> {
		return;
	}

	public postErrorLogRequest(request: ErrorLogRequest): Observable<any> {
		this.setDynamicTimeout(AppConfigUtil.getProperties().timeOut);
		this.setDynamicUri(
			StringUtil.format(
				DMVConstants.ERROR_LOG,
				request.agencyID,
				request.sessionID
			)
		);

		return this.postErrorLog(request.errorDescription);
	}
}
